import clsx from 'clsx';
import { Icon } from 'common/icon';
import Image from 'next/image';
import React, { useMemo, useState } from 'react';
import styles from './styles.module.css';

import { IQuestion, IReply } from 'constants/types';

import { useLoginModal } from 'hooks';

import { BlurHashImage } from 'components/blurhash-image';
import { LoginModal } from 'components/modal/login';
import { ReplyTopic } from 'components/template/publication/comment/replies';
import dayjs from 'dayjs';
import Link from 'next/link';
import { Routes } from 'constants/routes';
import { getItemIdentifier } from 'utils/slug';
import { ImageModal } from 'components/modal/image';

interface AvatarProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
}

export const Avatar = ({
  src,
  alt,
  width = 50,
  height = 50,
  className,
}: AvatarProps) => {
  return (
    <div className={styles.avatar_container}>
      <Image
        width={width}
        height={height}
        src={src}
        alt={alt}
        className={clsx(styles.avatar, className)}
      />
    </div>
  );
};

interface ActionItemProps {
  icon: React.JSX.Element;
  text: string;
  action?: (reply: IQuestion) => void;
  item?: IQuestion | undefined;
  isActive?: boolean;
}

export const ActionItem = ({
  icon,
  text,
  action,
  item,
  isActive,
}: ActionItemProps) => {
  const handleClick = () => {
    if (action && item) {
      action(item);
    }
  };
  return (
    <div
      className={clsx(styles.actionItem, { [styles.clicked]: isActive })}
      onClick={handleClick}
    >
      {icon}
      <div className={styles.actionText}>{text}</div>
    </div>
  );
};

interface CommentProps {
  data: IQuestion;
  reply?: IReply;
  onReplyClick?: (reply: IQuestion) => void;
  isActive?: boolean;
  useReply?: boolean;
}

export const CommentPost = ({
  data,
  isActive,
  onReplyClick,
  useReply = true,
}: CommentProps) => {
  const { handleCloseLoginModal, handleConfirmLoginModal, isLoginVisible } =
    useLoginModal();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const timeAgo = useMemo(() => {
    const now = dayjs();
    const targetDate = dayjs(data.createdAt);
    const diffInMinutes = now.diff(targetDate, 'minute');
    const diffInHours = now.diff(targetDate, 'hour');
    const diffInDays = now.diff(targetDate, 'day');

    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${'mins ago'}`;
    } else if (diffInHours < 24) {
      return `${diffInHours} ${'h ago'}`;
    } else {
      return `${diffInDays} ${'days ago'}`;
    }
  }, [data.createdAt]);

  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  return (
    <div className={styles.container}>
      {data.user.imageUrl && (
        <Link href={Routes.publicProfile(getItemIdentifier(data.user))}>
          <Avatar
            src={data.user.imageUrl}
            alt={data.user.imageUrl}
            className={styles.avatar_comment}
          />
        </Link>
      )}
      <article className={styles.wrapper}>
        <header className={styles.header}>
          <div className={styles.user}>
            <div className={styles.avatar_wrapper}>
              <div className={styles.info}>
                <h2 className={styles.name}>{data.user.username}</h2>
                <div className={styles.divider} />
                <time className={styles.date}>{timeAgo}</time>
              </div>
              <div className={styles.actionBar}>
                {useReply && (
                  <ActionItem
                    icon={<Icon.ArrowStraight />}
                    text="Reply"
                    isActive={isActive}
                    item={data as IQuestion}
                    action={onReplyClick}
                  />
                )}
              </div>
            </div>
            <div className={styles.content}>
              {data.reply && <ReplyTopic reply={data.reply} />}
              <p className={styles.text}>{data.message}</p>
              {data.imageUrl && (
                <div
                  className={styles.attachedImage}
                  onClick={openImageModal}
                  style={{ cursor: 'pointer' }}
                >
                  <BlurHashImage
                    src={data.imageUrl ?? ''}
                    alt="Attached image"
                    width={100}
                    height={100}
                    blurhash={data.blurHash}
                  />
                </div>
              )}
            </div>
            <LoginModal
              isVisible={isLoginVisible}
              onClose={handleCloseLoginModal}
              confirmOnClick={handleConfirmLoginModal}
            />

            <ImageModal
              isOpen={isImageModalOpen}
              imageUrl={data.imageUrl || ''}
              onClose={closeImageModal}
            />
          </div>
        </header>
      </article>
    </div>
  );
};
